// ADR 5.9.26p §2 — the half of the two panels that is identical.
//
// Sign-in and registration are one protocol and one form; they differ by the
// terms checkbox and the endpoint. Both live here so `SignInPanel` and
// `RegisterPanel` stay short enough to read in one screen, and so a change to
// the labelling or the busy state cannot land on one door and miss the other.

import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '../api/http'
import { Field } from '../ui/Field'
import { loginCompleted, registrationCompleted, registrationStarted } from '../telemetry/auth'
import { signedIn } from '../telemetry/identity'
import { submitCredentials, type CredentialIntent, type CredentialStage } from './credentials'
import { useAuthSession } from './session'
import { warmWasm } from '../wasm/preloadHint'

export interface CredentialValues {
  email: string
  password: string
}

export interface CredentialForm {
  values: CredentialValues
  setValues: (values: CredentialValues) => void
  /** Which second of the sign-in cost is being paid, or `null` when idle. */
  stage: CredentialStage | null
  busy: boolean
  /** The server's own (already localised) message, or `null`. */
  error: string | null
  submit: (event: FormEvent) => Promise<void>
}

/**
 * Announce a session this door just minted (ADR 6.9.26j §1/§1b).
 *
 * The two intents differ in three ways at once — which event, whether `alias`
 * fires, and (registration only) that a `registration_started` preceded it —
 * so the branch lives here rather than being spelt out twice at the call site.
 * Both halves emit *after* `adopt()`: an `AuthResponse` that the context
 * refused to take is not a sign-in.
 */
function announceSession(intent: CredentialIntent, userId: string): void {
  if (intent === 'register') {
    registrationCompleted()
  } else {
    loginCompleted('email')
  }
  signedIn(userId, intent === 'register')
}

/**
 * The submit half: challenge → proof-of-work → prehash → the endpoint, then
 * hand the minted session to the context.
 *
 * A failure is shown, never retried (§2): the proof-of-work is spent, so an
 * automatic second attempt would cost the user seconds and very likely be
 * refused again.
 *
 * ADR 6.9.26j §1: `SignInPanel` and `RegisterPanel` both submit through this
 * one hook, so both doors are instrumented from one file — which is why gap 1
 * names `CredentialFields.tsx` and not the panels.
 */
export function useCredentialSubmit(intent: CredentialIntent): CredentialForm {
  const { adopt } = useAuthSession()
  const [values, setValues] = useState<CredentialValues>({ email: '', password: '' })
  const [stage, setStage] = useState<CredentialStage | null>(null)
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    // Before the request, so the funnel's first step counts attempts rather
    // than successes. A registration that is refused still started.
    if (intent === 'register') registrationStarted()
    try {
      const response = await submitCredentials({ intent, ...values, onStage: setStage })
      await adopt(response)
      announceSession(intent, response.user_id)
    } catch (cause) {
      // The backend localises its own error bodies through `t()`, so its text
      // is better than anything this client could substitute for it.
      setError(cause instanceof ApiError ? cause.message : '')
    } finally {
      setStage(null)
    }
  }

  return { values, setValues, stage, busy: stage !== null, error, submit }
}

// ADR 8.9.26g §3 — both doors' fields through `ui/Field`, which owns the
// control. The `FIELD_CLASS` constant this replaced was one of three drifted
// copies of "what an input looks like"; the label association it used to get
// from wrapping the control now comes from `useId` and `htmlFor`.
//
// ADR 24.9.26l §5 — `passwordAutoComplete`: register passes `new-password`, so a
// password manager offers to generate one (WCAG 1.3.5, and 3.3.8's "no memory
// step"); sign-in keeps `current-password` by default.
export function EmailPasswordFields({
  form,
  passwordAutoComplete = 'current-password',
}: {
  form: CredentialForm
  passwordAutoComplete?: 'current-password' | 'new-password'
}) {
  const { t } = useTranslation()
  const { values, setValues, busy } = form
  return (
    // ADR 25.9.26q §3 — `onFocus` bubbles (React listens for `focusin`), so one
    // handler warms the wasm for both fields; `space-y-3` matches both panels'
    // `<form>`, so the spacing a fragment inherited is unchanged.
    <div className="space-y-3" onFocus={warmWasm}>
      <Field
        label={t('auth_email')}
        type="email"
        autoComplete="email"
        required
        disabled={busy}
        value={values.email}
        onChange={(email) => setValues({ ...values, email })}
      />
      <Field
        label={t('auth_password')}
        type="password"
        autoComplete={passwordAutoComplete}
        required
        disabled={busy}
        value={values.password}
        onChange={(password) => setValues({ ...values, password })}
      />
    </div>
  )
}

/**
 * The cost, shown rather than hidden (§2). Every stage is seconds of real CPU,
 * so a bare spinner would read as a hung page.
 */
export function CredentialStatus({ form }: { form: CredentialForm }) {
  const { t } = useTranslation()
  if (form.stage !== null) {
    return <p className="text-sm text-ink-muted">{t('loading')}</p>
  }
  if (form.error !== null) {
    return (
      <p role="alert" className="text-sm text-danger">
        {form.error === '' ? t('error_unknown') : form.error}
      </p>
    )
  }
  return null
}
